import React, { useState } from 'react'
import { Icon, Button, Autocomplete, Popover, Dialog } from '@equinor/eds-core-react'
import { close } from '@equinor/eds-icons'
import styled from 'styled-components'
import {
    AreaDto,
    DeviceDto,
    ProjectDto,
    StatusDto,
    SuperiorTagDto,
    SystemDto,
    BatteryStatus,
    SignalQuality,
} from '@/core/models/types'
import { BATTERY_STATUS_OPTIONS, SIGNAL_QUALITY_OPTIONS, HealthFilterOption } from '@/core/models/sensorHealth'
import { baseFont, InputStyledLabel, ClosedStyledLabel, EdsStyledLabel } from '@/styles/layout'
import {
    useGetAreasService,
    useGetSuperiorTagsService,
    useGetSystemsService,
} from '@/core/hooks-and-services/onboardedSensorsService'
import { useAtomValue } from 'jotai'
import { currentPlantAtom } from '@/core/store/globalStore'
import { close as closeIcon } from '@equinor/eds-icons'

type FilterableType =
    | 'status'
    | 'device'
    | 'protocol'
    | 'project'
    | 'system'
    | 'area'
    | 'superiorTag'
    | 'batteryStatus'
    | 'signalQuality'
    | 'sensorType'

interface FilterDialogProps {
    showMenu: boolean
    onClose: () => void
    statuses: StatusDto[]
    projects: ProjectDto[]
    devices: DeviceDto[]
    protocols: string[]
    projectsLoading: boolean
    protocolsLoading: boolean
    onSelectionChange: (type: FilterableType, changes: any) => void
    onClear: (type: FilterableType) => void
    selectedStatus: StatusDto[]
    selectedDevice: DeviceDto | null
    selectedProtocol: string | ''
    selectedProject: ProjectDto | null
    selectedSystem: SystemDto | null
    selectedArea: AreaDto | null
    selectedSuperiorTag: SuperiorTagDto | null
    selectedBatteryStatus: BatteryStatus | null
    selectedSignalQuality: SignalQuality | null
    selectedSensorType: string | null
    anchor: HTMLElement | null
}

const FilterDialog: React.FC<FilterDialogProps> = ({
    showMenu,
    onClose,
    statuses,
    projects,
    devices,
    protocols,
    projectsLoading,
    protocolsLoading,
    onSelectionChange,
    onClear,
    selectedStatus,
    selectedDevice,
    selectedProtocol,
    selectedProject,
    selectedSystem,
    selectedArea,
    selectedSuperiorTag,
    selectedBatteryStatus,
    selectedSignalQuality,
    selectedSensorType,
    anchor,
}) => {
    const [windowWidth] = useState(window.innerWidth)

    const currentPlant = useAtomValue(currentPlantAtom)
    const isAllPlants = currentPlant == null

    const { data: systems = [], isLoading: systemsLoading } = useGetSystemsService(currentPlant?.shortName || '')
    const { data: areas = [], isLoading: areasLoading } = useGetAreasService(currentPlant?.shortName || '')
    const { data: superiorTags = [], isLoading: superiorTagsLoading } = useGetSuperiorTagsService(
        currentPlant?.shortName || ''
    )

    if (!showMenu) return null

    const portfolioDevices = devices.filter((device) => device.portfolio)

    const filterTitle = 'Filter'
    const perPlantHelperText = isAllPlants ? 'Select a plant in the top bar to enable this filter.' : undefined

    const selectedBatteryOption =
        BATTERY_STATUS_OPTIONS.find((option) => option.value === selectedBatteryStatus) ?? null
    const selectedSignalOption = SIGNAL_QUALITY_OPTIONS.find((option) => option.value === selectedSignalQuality) ?? null

    // This is a fix for EDS Autocomplete filters not working correctly when routed from Plant Status drilldowns (avoid duplicating filters)
    const selectedStatusOptions = selectedStatus.map(
        (selected) => statuses.find((option) => option.value === selected.value) ?? selected
    )

    // Distinct sensor types (device measurement types) available across the portfolio devices.
    const sensorTypeOptions = Array.from(
        new Set(
            devices
                .map((device) => device.deviceMeasurementType?.trim())
                .filter((type): type is string => Boolean(type))
        )
    ).sort((a, b) => a.localeCompare(b))

    const renderContent = () => (
        <StyledContainer>
            <StyledAutocomplete
                multiple
                label={
                    <RowStyle>
                        <InputStyledLabel>Status</InputStyledLabel>
                    </RowStyle>
                }
                options={statuses}
                optionLabel={(status: unknown) => (status as StatusDto).displayName ?? ''}
                selectedOptions={selectedStatusOptions}
                onOptionsChange={(status) => onSelectionChange('status', { selectedItems: status.selectedItems })}
                onClear={() => onClear('status')}
            />
            <StyledAutocomplete
                label={
                    <RowStyle>
                        <InputStyledLabel>Device</InputStyledLabel>
                    </RowStyle>
                }
                options={portfolioDevices}
                optionLabel={(device: unknown) => (device as DeviceDto).displayName ?? ''}
                selectedOptions={selectedDevice ? [selectedDevice] : []}
                onOptionsChange={(changes) =>
                    onSelectionChange('device', {
                        selectedItems: changes.selectedItems.length > 0 ? changes.selectedItems : [],
                    })
                }
                onClear={() => onClear('device')}
                loading={portfolioDevices.length === 0}
            />
            <StyledAutocomplete
                label={
                    <RowStyle>
                        <InputStyledLabel>Sensor type</InputStyledLabel>
                    </RowStyle>
                }
                options={sensorTypeOptions}
                optionLabel={(sensorType: unknown) => sensorType as string}
                selectedOptions={selectedSensorType ? [selectedSensorType] : []}
                onOptionsChange={(changes) =>
                    onSelectionChange('sensorType', {
                        selectedItems: changes.selectedItems.length > 0 ? changes.selectedItems : [],
                    })
                }
                onClear={() => onClear('sensorType')}
                loading={sensorTypeOptions.length === 0}
            />
            <StyledAutocomplete
                label={
                    <RowStyle>
                        <InputStyledLabel>Protocol</InputStyledLabel>
                    </RowStyle>
                }
                options={protocols}
                optionLabel={(protocol: unknown) => protocol as string}
                selectedOptions={selectedProtocol ? [selectedProtocol] : []}
                onOptionsChange={(changes) =>
                    onSelectionChange('protocol', {
                        selectedItems: changes.selectedItems.length > 0 ? changes.selectedItems : [],
                    })
                }
                onClear={() => onClear('protocol')}
                loading={protocolsLoading}
            />
            <StyledAutocomplete
                label={
                    <RowStyle>
                        <InputStyledLabel>Project</InputStyledLabel>
                    </RowStyle>
                }
                options={projects}
                optionLabel={(project: unknown) => (project as ProjectDto).projectCode ?? 'No Project Code'}
                selectedOptions={selectedProject ? [selectedProject] : []}
                onOptionsChange={(changes) =>
                    onSelectionChange('project', {
                        selectedItems: changes.selectedItems.length > 0 ? changes.selectedItems : [],
                    })
                }
                onClear={() => onClear('project')}
                disabled={isAllPlants}
                helperText={perPlantHelperText}
                loading={!isAllPlants && projectsLoading}
            />
            <StyledAutocomplete
                label={
                    <RowStyle>
                        <InputStyledLabel>Superior tag</InputStyledLabel>
                    </RowStyle>
                }
                options={superiorTags}
                optionLabel={(tag: unknown) => (tag as SuperiorTagDto).superiorTag ?? 'No Superior Tag'}
                selectedOptions={selectedSuperiorTag ? [selectedSuperiorTag] : []}
                onOptionsChange={(changes) =>
                    onSelectionChange('superiorTag', {
                        selectedItems: changes.selectedItems.length > 0 ? changes.selectedItems : [],
                    })
                }
                onClear={() => onClear('superiorTag')}
                disabled={isAllPlants}
                helperText={perPlantHelperText}
                loading={!isAllPlants && superiorTagsLoading}
            />
            <StyledAutocomplete
                label={
                    <RowStyle>
                        <InputStyledLabel>System</InputStyledLabel>
                    </RowStyle>
                }
                options={systems}
                optionLabel={(system: unknown) => (system as SystemDto).systemCode ?? ''}
                selectedOptions={selectedSystem ? [selectedSystem] : []}
                onOptionsChange={(changes) =>
                    onSelectionChange('system', {
                        selectedItems: changes.selectedItems.length > 0 ? changes.selectedItems : [],
                    })
                }
                onClear={() => onClear('system')}
                disabled={isAllPlants}
                helperText={perPlantHelperText}
                loading={!isAllPlants && systemsLoading}
            />
            <StyledAutocomplete
                label={
                    <RowStyle>
                        <InputStyledLabel>Area</InputStyledLabel>
                    </RowStyle>
                }
                options={areas}
                optionLabel={(system: unknown) => (system as AreaDto).areaCode ?? ''}
                selectedOptions={selectedArea ? [selectedArea] : []}
                onOptionsChange={(changes) =>
                    onSelectionChange('area', {
                        selectedItems: changes.selectedItems.length > 0 ? changes.selectedItems : [],
                    })
                }
                onClear={() => onClear('area')}
                disabled={isAllPlants}
                helperText={perPlantHelperText}
                loading={!isAllPlants && areasLoading}
            />
            <StyledAutocomplete
                label={
                    <RowStyle>
                        <InputStyledLabel>Battery status</InputStyledLabel>
                    </RowStyle>
                }
                options={BATTERY_STATUS_OPTIONS}
                optionLabel={(option: unknown) => (option as HealthFilterOption<BatteryStatus>).label}
                selectedOptions={selectedBatteryOption ? [selectedBatteryOption] : []}
                onOptionsChange={(changes) =>
                    onSelectionChange('batteryStatus', {
                        selectedItems: changes.selectedItems.length > 0 ? changes.selectedItems : [],
                    })
                }
                onClear={() => onClear('batteryStatus')}
            />
            <StyledAutocomplete
                label={
                    <RowStyle>
                        <InputStyledLabel>Signal strength</InputStyledLabel>
                    </RowStyle>
                }
                options={SIGNAL_QUALITY_OPTIONS}
                optionLabel={(option: unknown) => (option as HealthFilterOption<SignalQuality>).label}
                selectedOptions={selectedSignalOption ? [selectedSignalOption] : []}
                onOptionsChange={(changes) =>
                    onSelectionChange('signalQuality', {
                        selectedItems: changes.selectedItems.length > 0 ? changes.selectedItems : [],
                    })
                }
                onClear={() => onClear('signalQuality')}
            />
        </StyledContainer>
    )

    if (anchor == null) {
        return (
            <FilterStepperContainer open={showMenu} onClose={onClose} isDismissable>
                <button /* Invisible button to avoid auto focus upon entering dialog container*/
                    tabIndex={-1}
                    style={{
                        position: 'absolute',
                        width: 1,
                        height: 1,
                        overflow: 'hidden',
                        opacity: 0,
                    }}
                />
                <MenuHeader>
                    <EdsStyledLabel group="heading" variant={windowWidth <= 1024 ? 'h4' : 'h5'}>
                        {filterTitle}
                    </EdsStyledLabel>
                    <CloseButton as="Link" onClick={onClose}>
                        {windowWidth < 1024 && <ClosedStyledLabel>Close</ClosedStyledLabel>}
                        <StyledIcon data={close} size={24} />
                    </CloseButton>
                </MenuHeader>
                <StepContent>{renderContent()}</StepContent>
            </FilterStepperContainer>
        )
    }

    return (
        <Popover open={showMenu} anchorEl={anchor} onClose={onClose}>
            <Popover.Header>
                <Popover.Title>{filterTitle}</Popover.Title>
                <Button
                    style={{ height: '32px', width: '32px' }}
                    variant="ghost_icon"
                    aria-label="Close popover"
                    onClick={onClose}
                >
                    <Icon data={closeIcon} size={24} />
                </Button>
            </Popover.Header>
            <Popover.Content>{renderContent()}</Popover.Content>
        </Popover>
    )
}

const FilterStepperContainer = styled(Dialog)`
    display: flex;
    flex-direction: column;
    width: 638px;
    height: auto;
    overflow-x: hidden;

    @media (max-width: 1024px) {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 82px;
        top: auto;
        height: auto;
        max-height: calc(100vh - 64px - 75px - 64px - 16px - 16px - 16px - 8px);
        overflow-y: auto;
        overflow-x: hidden;
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;
        width: 100%;
        background: #ffffff;
        box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15);
        transform: none;
        border-radius: 0px;
    }
`

const MenuHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-left: 16px;

    button {
        background: none;
        border: none;
        cursor: pointer;
    }
    @media (max-width: 1024px) {
        color: #1d1d1d;
        margin-left: 0;
        margin-right: 0;
        padding-left: 16px;
        padding-right: 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid #dcdcdc;
    }
`

const StepContent = styled.div`
    margin: 0rem 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    flex-grow: 1;
    margin-bottom: 16px;
`

const CloseButton = styled(Button)`
    ${baseFont}
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px;
    text-decoration: none;
    background: none;
    border: none;
    cursor: pointer;
    color: #007079;

    &:hover {
        background: #eaeaea;
    }

    @media (max-width: 1024px) {
        color: #1f6369;

        &:hover {
            background: #f5f5f5;
        }
    }
`
const StyledIcon = styled(Icon)`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #007079;

    @media (max-width: 1024px) {
        color: #1f6369;
        &:hover {
            background: #f5f5f5;
            color: #1f6369;
        }
    }
`

const StyledContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 0px;
    gap: 16px;
    @media (max-width: 1024px) {
        margin-right: 16px;
        margin-left: 16px;
    }
`

const StyledAutocomplete = styled(Autocomplete).attrs({ autoWidth: true })`
    ${baseFont}
    @media (max-width: 1024px) {
        /* Exclude the option-list checkboxes, which must keep the dialog background. */
        input:not([type='checkbox']) {
            background-color: #f5f5f5;
            color: #1d1d1d;
        }
    }
`

const RowStyle = styled.div`
    display: flex;
    align-items: center;
    flex-direction: row;
    margin-bottom: 4px;
    gap: 8px;
    > * {
        margin-right: 0.3rem;
    }
`

export default FilterDialog
